(ns app.front.view.meetings.subs
  (:require [app.domain.committee.model :as committee-model]
            [app.front.entities :as entities]
            [app.front.query.core :as query]
            [app.front.view.committees.subs :as committee-subs]
            [clojure.set :as set]
            [core.front.date-util :as date-util]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(defn- default-date-range []
  (let [now (js/Date.)]
    {:date-from (date-util/start-of-day (date-util/add-days-to-date now -365))
     :date-to   (date-util/end-of-day (date-util/add-days-to-date now 365))}))

(rf/reg-event-fx ::fetch-committees-and-meetings
  (fn [{:keys [db]} [_ group-id]]
    (let [{:keys [date-from date-to]} (default-date-range)]
      {:db           (-> db
                         (assoc ::loading? true)
                         (update ::meetings-filters merge {:date-from date-from
                                                           :date-to   date-to}))
       :comm/command {:command    :committee/committees
                      :data       (util/mmap group-id)
                      :on-success [::fetch-committees.success group-id]}})))

(rf/reg-event-fx ::fetch-committees.success
  (fn [_ [_ group-id status resp]]
    {:fx [[:dispatch [::entities/merge-entity-db status (:entities resp)]]
          [:dispatch [::fetch-all-meetings group-id]]]}))

(rf/reg-event-fx ::fetch-all-meetings
  (fn [{:keys [db]} [_ group-id]]
    (let [ctx        (entities/db->context db)
          committees (entities/find-entities ctx {:entity/type   :entity.type/committee
                                                  :entity/parent group-id})
          filters    (::meetings-filters db)
          start      (:date-from filters)
          end        (:date-to filters)]
      (if (seq committees)
        {:dispatch-n (conj (mapv (fn [committee]
                                   [::query/fetch
                                    :committee/meetings
                                    {:committee-id (:db/id committee)
                                     :start        start
                                     :end          end}])
                                 committees)
                           [::clear-loading])}
        {:db (dissoc db ::loading?)}))))

(rf/reg-event-db ::clear-loading
  (fn [db _]
    (dissoc db ::loading?)))

(rf/reg-sub ::loading-committees?
  (fn [db _]
    (::loading? db false)))

(rf/reg-sub ::loading?
  :<- [::loading-committees?]
  :<- [::query/fetching? :committee/meetings]
  (fn [[loading-committees? fetching-meetings?] _]
    (or loading-committees? fetching-meetings?)))

(rf/reg-sub ::all-meetings
  (fn [[_ group-id] _]
    [(rf/subscribe [::committee-subs/committees-in-group group-id])
     (rf/subscribe [::entities/context])])
  (fn [[committees ctx] _]
    (->> committees
         (mapcat (fn [committee]
                   (entities/find-entities ctx {:entity/type   :entity.type/committee-meeting
                                                :entity/parent (:db/id committee)})))
         (sort-by :committee-meeting/date))))

(rf/reg-sub ::committee-authority-groups
  (fn [[_ group-id] _]
    (rf/subscribe [::committee-subs/committees-in-group group-id]))
  (fn [committees _]
    (->> committees
         (mapcat :committee/authority-groups)
         distinct
         (sort-by :group/name))))

(rf/reg-event-fx ::set-meetings-filter
  (fn [{:keys [db]} [_ key val]]
    (let [val (case key
                :date-from (date-util/start-of-day (or val (:date-from (default-date-range))))
                :date-to   (date-util/end-of-day (or val (:date-to (default-date-range))))
                val)]
      (cond-> {:db (assoc-in db [::meetings-filters key] val)}
        (#{:date-from :date-to} key)
        (assoc :dispatch-debounce [{:id      ::refetch-for-date-filter
                                    :timeout 500
                                    :action  :dispatch
                                    :event   [::fetch-meetings-for-filters]}])))))

(rf/reg-event-fx ::fetch-meetings-for-filters
  (fn [{:keys [db]} _]
    (let [group-id      (:app.front.view.group.admin.subs/group-id db)
          filters       (::meetings-filters db)
          start         (:date-from filters)
          end           (:date-to filters)
          ctx           (entities/db->context db)
          committees    (entities/find-entities ctx {:entity/type   :entity.type/committee
                                                     :entity/parent group-id})
          meeting-types (:meeting-types filters)
          committees    (if (seq meeting-types)
                          (filter (comp meeting-types :db/id) committees)
                          committees)]
      (when (seq committees)
        {:dispatch-n (mapv (fn [committee]
                             [::query/fetch
                              :committee/meetings
                              {:committee-id (:db/id committee)
                               :start        start
                               :end          end}])
                           committees)}))))

(rf/reg-sub ::meetings-filters
  :-> ::meetings-filters)

(rf/reg-sub ::filtered-meetings
  (fn [[_ group-id] _]
    [(rf/subscribe [::all-meetings group-id])
     (rf/subscribe [::meetings-filters])])
  (fn [[meetings {:keys [departments statuses meeting-types date-from date-to]}] _]
    (cond->> meetings
      (not-empty departments)
      (filter (fn [m] (-> m
                          committee-model/meeting-authority-groups
                          (set/intersection departments)
                          seq)))

      (not-empty statuses)
      (filter (fn [m] (contains? statuses (:domain/state m))))

      (not-empty meeting-types)
      (filter (fn [m] (contains? meeting-types (-> m :entity/parent :db/id))))

      date-from
      (filter (fn [m] (some-> m :committee-meeting/date
                              (date-util/on-or-after? date-from))))

      date-to
      (filter (fn [m] (some-> m :committee-meeting/date
                              (date-util/on-or-before? date-to)))))))

(rf/reg-event-db ::show-edit-meeting
  (fn [db [_ meeting-id]]
    (assoc-in db [::edit-meeting-status meeting-id] :visible)))

(rf/reg-event-db ::hide-edit-meeting
  (fn [db [_ meeting-id]]
    (update db ::edit-meeting-status dissoc meeting-id)))

(rf/reg-sub ::edit-meeting-status
  (fn [db [_ meeting-id]]
    (get-in db [::edit-meeting-status meeting-id])))

(rf/reg-event-fx ::save-meeting
  (fn [{:keys [db]} [_ meeting-id]]
    (let [changes (get-in db [:app.front.entity-edit/unsaved meeting-id])]
      (when (seq changes)
        {:comm/command {:command    :committee/edit-meeting
                        :data       {:id      meeting-id
                                     :changes changes}
                        :on-success [::save-meeting.success meeting-id]
                        :notify     {:success :meetings/edit-meeting-success}}}))))

(rf/reg-event-fx ::save-meeting.success
  (fn [{:keys [db]} [_ meeting-id status resp]]
    {:db         (update db :app.front.entity-edit/unsaved dissoc meeting-id)
     :dispatch-n [[::entities/apply-changes status resp]
                  [::hide-edit-meeting meeting-id]]}))
